import type { CommonCenturyObject, SchemeVersionInfo } from './century-objects';
import type { CenturyFilterArray, CenturySort, ProxyType, StaticMeta } from './century-shared';

export const EXECTION_STATUS = {
    PENDING: 'W',
    INDEXING: 'I',
    PROCESSING: 'P',
    COMPLETE: 'C',
    ERROR: 'E',
} as const;

export type ExecutionStatus = typeof EXECTION_STATUS[keyof typeof EXECTION_STATUS];

export type CenturyQuerySchemaVersionRequest = {
    proxyType?: ProxyType;
    group?: string | null;
    owner?: string;
    version?: number | string;
    parseSchemaMsd?: boolean;
} & ({
    byName: true;
    profile: string;
    name: string;
} | {
    byName?: false;
    id: number | string;
});

export type CenturyQuerySchemaVersionResponse = {
    ret?: SchemeVersionInfo;
};

export type CenturyQuerySchemaVersionsByIdRequest = {
    proxyType?: ProxyType;
    group?: string | null;
    owner?: string;
    schemeVersionIds: (number | string)[];
    parseSchemaMsd?: boolean;
};

export type CenturyQuerySchemaVersionsByIdResponse = {
    ret: {
        list?: SchemeVersionInfo[];
    };
};

export type CenturyQueryResult = {
    exection_status: ExecutionStatus;
    exection_progress?: string | number | null;
    processing_object?: string | number | null;
    processing_object_max?: string | number | null;
    processing_action?: string | null;
    evaluated_error?: string;
    limit?: number | string;
    limitOverflow?: boolean;
    documentCount?: number;
    creationTimeMin?: string;
    creationTimeMax?: string;
};

export type CenturyQueryNewRequest = {
    proxyType?: ProxyType;
    group?: string | null;
    owner?: string;
    referent?: string;
    filter: CenturyFilterArray;
    order?: CenturySort;
    limit?: number | string;
    queryPdv?: boolean;
    creationTimeMin?: string;
    creationTimeMax?: string;
};
export type CenturyQueryNewResponse = CenturyQueryResult & {
    id_query: number | string;
};

export type CenturyQueryStatusRequest = {
    id_query: number | string;
    queryPdv?: boolean;
};
export type CenturyQueryStatusResponse = CenturyQueryResult;

export type CommonCenturyDocument = CommonCenturyObject & {
    documentId: number | string;
    volumeId?: number | string;
    schemeVersionId?: number | string;
    metadata?: string;
    parsedMeta?: Record<string, string>;

    metadataUrl: string;
    metadataSize?: number | string;
    metadataHash: string;
    stdMetadataUrl: string;
    stdMetadataSize?: number | string;
    stdMetadataHash: string;

    creationTime: string;
    submitTime: string;
    staticMeta: StaticMeta;
};

export type DocListItemInfo = CommonCenturyDocument & {
    sizeOfFiles?: number | string;
    warningCount?: number | string;
};

export type DocPartInfo = CommonCenturyDocument & {
    id: number | string;

    dataUrl: string;
    dataSize?: number | string;
    dataHash: string;
    dataContentType: string;

    warningMessage: string;
};

export type DocAndPartListItem = DocListItemInfo & {
    documentPart?: DocPartInfo;
};

export type CenturyQueryDocumentListRequest = {
    id_query: number | string;
    pageOffset?: number | string;
    pageSize?: number | string;
};
export type CenturyQueryDocumentListResponse<T> = {
    docList: T[];
};

export type CenturyQueryDocumentByIdRequest = {
    proxyType?: ProxyType;
    group?: string | null;
    owner?: string;
    documentId: number | string;
};

export type CenturyQueryDocumentByIdResponse = {
    document: DocListItemInfo;
    scheme: SchemeVersionInfo;
};

export type CenturyPdDNewRequest = {
    id_query: number | string;
    queryPdv?: boolean;
    description: string;
    volumePddPassword?: string;
};
export type CenturyPdDNewResponse = {
    id: number | string;
};

export interface CenturySearchApi {
    'CenturyQuerySchemaVersion': {
        request: CenturyQuerySchemaVersionRequest;
        response: CenturyQuerySchemaVersionResponse;
    };
    'CenturyQuerySchemaVersionsById': {
        request: CenturyQuerySchemaVersionsByIdRequest;
        response: CenturyQuerySchemaVersionsByIdResponse;
    };
    'CenturyQueryNew': {
        request: CenturyQueryNewRequest;
        response: CenturyQueryNewResponse;
    };
    'CenturyQueryStatus': {
        request: CenturyQueryStatusRequest;
        response: CenturyQueryStatusResponse;
    };
    'CenturyQueryDocumentList': {
        request: CenturyQueryDocumentListRequest;
        response: CenturyQueryDocumentListResponse<DocListItemInfo>;
    };
    'CenturyQueryDocumentPartList': {
        request: CenturyQueryDocumentListRequest;
        response: CenturyQueryDocumentListResponse<DocPartInfo>;
    };
    'CenturyQueryDocumentAndPartList': {
        request: CenturyQueryDocumentListRequest;
        response: CenturyQueryDocumentListResponse<DocAndPartListItem>;
    };
    'CenturyQueryDocumentById': {
        request: CenturyQueryDocumentByIdRequest;
        response: CenturyQueryDocumentByIdResponse;
    };
    'CenturyPdDNew': {
        request: CenturyPdDNewRequest;
        response: CenturyPdDNewResponse;
    };
}
